// @ts-check

import React, { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { Brain, ChevronDown } from 'lucide-react';
import { ChatMarkdown } from './ChatBubble';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { ErrorBoundary } from 'react-error-boundary';
import { cn } from '@/lib/utils';

/**
 * ChatBubbleReasoning component displays AI reasoning content with collapsible animated timeline
 * @param {Object} props
 * @param {Array<any>} props.normalisedParts - The normalized chat message parts
 * @param {boolean} [props.isStreaming=false] - Whether content is being streamed
 */
function ChatBubbleReasoning({ normalisedParts, isStreaming = false }) {
  return (
    <ErrorBoundary
      fallback={<div className="text-sm text-muted-foreground p-2">Unable to display reasoning content</div>}
      onError={(error, errorInfo) => {
        Sentry.captureException(error, {
          extra: {
            context: 'ChatBubbleReasoning - error boundary',
            componentStack: errorInfo.componentStack,
            normalisedPartsCount: normalisedParts?.length || 0,
            isStreaming,
            reasoningPartsCount: normalisedParts?.filter(part => part.type === "reasoning_tag" || part.type === "reasoning_content")?.length || 0
          }
        });
        console.error('ChatBubbleReasoning error:', error);
      }}
    >
      <ChatBubbleReasoningComponent normalisedParts={normalisedParts} isStreaming={isStreaming} />
    </ErrorBoundary>
  );
}

/**
 * Internal component with error boundary protection
 * @param {Object} props
 * @param {Array<any>} props.normalisedParts - The normalized chat message parts
 * @param {boolean} [props.isStreaming=false] - Whether content is being streamed
 */
function ChatBubbleReasoningComponent({ normalisedParts, isStreaming = false }) {
  const [isExpanded, setIsExpanded] = useState(isStreaming);

  useEffect(() => {
    if (!isStreaming) setIsExpanded(false);
  }, [isStreaming]);

  const reasoningParts = normalisedParts.filter(
    part => part.type === "reasoning_tag" || part.type === "reasoning_content"
  );

  if (!reasoningParts.length) return null;

  const content = reasoningParts.map(part => part.data.content);

  if (!content.length) return null;

  return (
    <div
      className={cn(
        "not-prose rounded-xl bg-transparent text-card-foreground overflow-hidden mb-3"
      )}
    >
      <Collapsible 
        open={isExpanded}
        onOpenChange={setIsExpanded}
      >
        <CollapsibleTrigger className={cn(
          "group flex w-full items-center px-4 py-3 font-medium bg-transparent",
          "transition-all duration-200 focus-visible:bg-muted/50",
          "text-sm text-muted-foreground focus-visible:text-foreground"
        )}>
          <div className="flex items-center gap-3">
            <Brain className="h-4 w-4 text-primary transition-colors" />
            <span className="font-normal text-foreground/90">Show reasoning</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground font-light transition-all duration-300 group-data-[state=open]:rotate-180" />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
          <div className="flex flex-col px-4 py-3 gap-2">
            <div className="relative pl-8">
              {content.map((reasoning, index) => {
                 const text = reasoning.trim();

                 return (
                   <div 
                     key={index} 
                     className="relative flex min-w-0"
                   >
                    {/* Timeline */}
                    <div className="absolute -left-6 top-0 flex flex-col items-center h-full">
                       <Brain className="w-4 h-4 mt-1 text-foreground shrink-0" />
                      {index < content.length - 1 && (
                        <div
                          className="w-px bg-border flex-1 mt-2 mb-2 min-h-[1rem]"
                        />
                      )}
                    </div>

                    <div 
                       className={cn(
                         "flex-1 min-w-0",
                         index < content.length - 1 ? "pb-4" : "pb-0"
                       )}
                     >
                      <div className="text-sm pt-1 overflow-x-auto overflow-y-visible opacity-60">
                        <ChatMarkdown isStreamingText={isStreaming}>
                          {text}
                        </ChatMarkdown>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default ChatBubbleReasoning;
